import { isEqual } from '@backmarket/utils/object/isEqual'

import type { ApplePayJS } from '../../../../../types/apple-pay'
import type { PaymentRequestDetailsUpdate } from '../../../types/PaymentRequest'

import { mapAddressErrorsToApplePayErrors } from './mapAddressErrorsToApplePayErrors'
import {
  mapItemToApplePayLineItem,
  mapShippingOptionsToApplePayMethods,
} from './mapPaymentRequestToApplePay'

type ApplePaySheetSnapshot = {
  total: ApplePayJS.ApplePayLineItem
  lineItems?: ApplePayJS.ApplePayLineItem[]
  shippingMethods?: ApplePayJS.ApplePayShippingMethod[]
  selectedShippingMethodId?: string
}
/**
 * Tracks what the Apple Pay sheet is currently displaying so that partial
 * `PaymentRequestDetailsUpdate` updates are merged onto the
 * current state.
 */
export function createApplePaySheetState(
  request: ApplePayJS.ApplePayPaymentRequest,
) {
  // Snapshot of what the sheet currently shows. Updates merge onto this.
  const current: ApplePaySheetSnapshot = {
    total: request.total,
    lineItems: request.lineItems,
    shippingMethods: request.shippingMethods,
    selectedShippingMethodId: request.shippingMethods?.[0]?.identifier,
  }

  // Process a partial update onto the current state.
  // Returns the direct update object that can be passed to Apple Pay.
  function apply(update: PaymentRequestDetailsUpdate | undefined) {
    if (update?.total) {
      current.total = {
        label: update.total.label,
        amount: update.total.amount.amount,
      }
    }

    if (update?.displayItems) {
      current.lineItems = update.displayItems.map(mapItemToApplePayLineItem)
    }

    let shippingMethodsChanged = false

    if (update?.shippingOptions) {
      const nextShippingMethods = mapShippingOptionsToApplePayMethods(
        update.shippingOptions,
      )
      // The selected method is sorted first, so a selection change reorders the
      // list and is detected by the deep equality too.
      shippingMethodsChanged = !isEqual(
        current.shippingMethods,
        nextShippingMethods,
      )
      current.shippingMethods = nextShippingMethods
      // Apple auto-selects the first method when options change.
      current.selectedShippingMethodId = current.shippingMethods[0]?.identifier
    }

    return {
      newTotal: current.total,
      ...(current.lineItems && { newLineItems: current.lineItems }),
      ...(shippingMethodsChanged &&
        current.shippingMethods && {
          newShippingMethods: current.shippingMethods,
        }),
    }
  }

  return {
    get selectedShippingMethodId() {
      return current.selectedShippingMethodId
    },

    selectShippingMethod(identifier: string) {
      current.selectedShippingMethodId = identifier
    },

    toShippingContactUpdate(
      update: PaymentRequestDetailsUpdate | undefined,
    ): ApplePayJS.ApplePayShippingContactUpdate {
      const sheetUpdate = apply(update)

      const errors: ApplePayJS.ApplePayError[] = []

      if (update?.error) {
        errors.push(
          new ApplePayError('addressUnserviceable', undefined, update.error),
        )
      }

      if (update?.shippingAddressErrors) {
        errors.push(
          ...mapAddressErrorsToApplePayErrors(update.shippingAddressErrors),
        )
      }

      return {
        ...sheetUpdate,
        ...(errors.length > 0 && { errors }),
      }
    },

    toShippingMethodUpdate(
      update: PaymentRequestDetailsUpdate | undefined,
    ): ApplePayJS.ApplePayShippingMethodUpdate {
      return apply(update)
    },
  }
}
